import React from 'react';
import { ProgressItem } from './types';

interface ProgressPanelProps {
  progress: ProgressItem[] | null;
  onOpenSummary: () => void;
  children?: React.ReactNode; // extra sidebar content (Review Response: the letter card)
}

// Human labels for the narrative sections the agent tracks. Stable product facts.
const LABELS: Record<string, string> = {
  '1.1': 'Title',
  '1.4': 'Lay Abstract',
  '4.1': 'Animal Model',
  '4.2': 'Experimental Design',
  '4.3': 'Animal Numbers',
  '4.4': 'Non-Surgical Procedures',
  '4.5': 'Surgical Procedures',
  '4.6': 'Euthanasia',
  '5.8': 'Adverse Effects',
  '6.1': 'Non-Duplication',
  '6.2': 'Alternatives'
};

// Right-hand panel: narrative sections + whether each has a saved draft. Sections the
// agent can't save directly (draft-only) are marked so the user knows to paste those.
// A section revised during an IACUC review round shows "Revised" instead of "Drafted"
// (with the round once there is more than one), so the researcher can see at a glance
// what changed and needs re-entering in InfoEd.
function statusLabel(item: ProgressItem): string {
  if (item.revised_in_round) return item.revised_in_round > 1 ? `Revised R${item.revised_in_round}` : 'Revised';
  return item.status === 'drafted' ? 'Drafted' : '—';
}

const ProgressPanel: React.FC<ProgressPanelProps> = ({ progress, onOpenSummary, children }) => {
  return (
    <aside className="agent-progress">
      <h3>Narrative Sections</h3>
      <p className="agent-progress-hint">
        The agent can save the sections marked <span className="agent-progress-savable-key">●</span>.
        Others it drafts for you to paste into Protocol View. Everything else (yes/no, numbers)
        goes straight into InfoEd.
      </p>

      {progress === null ? (
        <p className="agent-progress-loading">Loading…</p>
      ) : (
        <ul className="agent-progress-list">
          {progress.map((item) => (
            <li key={item.question_number} className={`agent-progress-item ${item.status}`}>
              <span className="agent-progress-dot" title={item.writable ? 'Agent can save this' : 'Draft-only (paste into Protocol View)'}>
                {item.writable ? '●' : '○'}
              </span>
              <span className="agent-progress-num">{item.question_number}</span>
              <span className="agent-progress-label">{LABELS[item.question_number] || ''}</span>
              <span
                className={`agent-progress-status ${item.revised_in_round ? 'revised' : item.status}`}
                title={item.revised_in_round ? `Revised in response to IACUC review (round ${item.revised_in_round})` : undefined}
              >
                {statusLabel(item)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {children}

      <button className="agent-progress-open" onClick={onOpenSummary}>
        Summary
      </button>
    </aside>
  );
};

export default ProgressPanel;
